import type { NextRouter } from 'next/router';
import { useRouter } from 'next/router';
import React from 'react';
import { connect } from 'react-redux';
import { compose, mapProps } from 'recompose';
import { isDefined } from '@peloton/types';
import { FriendType } from '@engage/overview/models/FriendType';
import { useReduxState } from '@engage/redux';
import { useModalLabel } from '@members/copy';
import type { CopyIDs } from '@members/copy';
import type { ModalLabel } from '@members/copy/models.generated/ModalLabel';
import { FACEBOOK_MODAL_NAME } from '@members/facebook/redux/facebookModal';
import type { ModalProps } from './Modal';
import { Modal } from './Modal';
import type { ModalName, ModalSelectorState } from './redux';
import { getActiveModalName } from './redux';
import { toQueryParamsWithoutModal } from './sagas';

const getModalContent = (modalNames: ModalMap, modalName?: ModalName) =>
  isDefined(modalName) && modalNames.hasOwnProperty(modalName)
    ? modalNames[modalName]
    : undefined;

const handleClosing = (props: DispatchProps & Partial<ModalContent>) => {
  props.closeModal();
  if (isDefined(props.onCloseModal)) {
    props.onCloseModal();
  }
};

const getModalName = (modalName: string | undefined, router: NextRouter) => {
  const nonQueryParamModalNames = [
    FACEBOOK_MODAL_NAME,
    FriendType.Following,
    FriendType.Follower,
  ];

  return isDefined(modalName) && nonQueryParamModalNames.includes(modalName)
    ? modalName
    : (router.query.modal as string | undefined);
};

export const ConnectedModal: React.FC<React.PropsWithChildren<OwnProps>> = props => {
  const router = useRouter();
  const modalName = useReduxState(
    (state: ModalSelectorState) =>
      getActiveModalName(state) ?? (router.query.modal as string | undefined),
  );

  const closeModalHandler = (modalIdentifiers: string[]) =>
    router.push(
      {
        query: toQueryParamsWithoutModal(router.query, modalIdentifiers),
      },
      undefined,
      { scroll: false },
    );
  const modalContent = getModalContent(props.modalNames, modalName);

  if (isDefined(modalContent)) {
    const { component, ...modalProps } = modalContent;
    return (
      <Modal
        isOpen={true}
        children={component}
        contentLabel={modalProps.ariaLabelId}
        closeHandler={() =>
          handleClosing({
            closeModal: () => closeModalHandler(modalProps.modelIdentifiers!),
            onCloseModal: modalProps.onCloseModal,
          })
        }
        {...modalProps}
        {...props}
      />
    );
  }

  return <Modal isOpen={false} contentLabel="" />;
};

/**
 * @deprecated in favor of hook based ConnectedModal
 */

export const LegacyConnectedModal = compose<ModalProps, OwnProps & Partial<ModalProps>>(
  connect((state: ModalSelectorState) => ({
    modalName: getActiveModalName(state),
  })),
  mapProps((props: StateProps & DispatchProps & OwnProps) => {
    const router = useRouter();
    const modalName = getModalName(props.modalName, router);
    const modalContent = getModalContent(props.modalNames, modalName);

    const closeModalHandler = (modalIdentifiers: string[]) =>
      router.push(
        {
          query: toQueryParamsWithoutModal(router.query, modalIdentifiers),
        },
        undefined,
        { scroll: false },
      );

    if (isDefined(modalContent)) {
      const { component, ...modalProps } = modalContent;
      return {
        ...props,
        ...modalProps,
        children: component,
        contentLabel: useModalLabel(modalContent.ariaLabelId),
        closeHandler: () =>
          handleClosing({
            closeModal: () => closeModalHandler(modalProps.modelIdentifiers!),
            onCloseModal: modalProps.onCloseModal,
          }),
        isOpen: true,
      };
    } else {
      return { isOpen: false };
    }
  }),
)(Modal);

type ModalContent = {
  component: React.ReactElement<any>;
  modelIdentifiers?: string[];
  ariaLabelId: CopyIDs<typeof ModalLabel>;
  onCloseModal?(): void;
} & Partial<ModalProps>;
export type ModalMap = Record<ModalName, ModalContent>;
type OwnProps = { modalNames: ModalMap };
type StateProps = { modalName?: ModalName; isOpen?: boolean };
type DispatchProps = { closeModal(modalIdentifiers?: string[]): void };
